import { useCallback, useSyncExternalStore } from "react";
import { usePage } from "@inertiajs/react";
import { useDismissAppNotification } from "@api/appNotifications.hooks";
import type { SharedPageProps } from "~/layouts/site-layout";

export interface AppNotificationData {
  id: number;
  notification_type: string;
  metadata: Record<string, unknown>;
}

/**
 * Module-level set that survives component remounts within the same SPA session.
 * Tracks notifications closed (session-only) or dismissed (permanently via API).
 * Cleared on full page reload since it's just an in-memory Set.
 */
const hiddenNotifications = new Set<string>();
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function hideNotification(type: string) {
  hiddenNotifications.add(type);
  listeners.forEach((l) => l());
}

function isHidden(type: string) {
  return hiddenNotifications.has(type);
}

/**
 * Hook to check if a specific notification type is active and provide dismiss functionality.
 * Returns null notification if not found, already dismissed, or closed this session.
 *
 * Uses module-level state so closing/dismissing survives component remounts
 * (e.g., loading state transitions that unmount/remount the component).
 */
export function useAppNotification(type: string) {
  const { app_notifications = [] } = usePage<SharedPageProps>().props;

  // Subscribe to module-level hidden state so re-renders happen on hide
  const hidden = useSyncExternalStore(
    subscribe,
    () => isHidden(type),
    () => isHidden(type)
  );

  const notification = app_notifications.find(
    (n) => n.notification_type === type
  );

  const { mutate, isPending } = useDismissAppNotification({
    onSuccess: () => hideNotification(type),
  });

  const dismiss = useCallback(() => {
    if (notification) {
      mutate(notification.id);
    }
  }, [notification, mutate]);

  const close = useCallback(() => {
    hideNotification(type);
  }, [type]);

  return {
    notification: hidden ? null : notification ?? null,
    dismiss,
    close,
    isDismissing: isPending,
  };
}
